import { Build, Component, Element, Host, Prop, Event, EventEmitter, State, Watch, h } from '@stencil/core';
import { metrics } from '../../utils';
import { Fills, CustomSizes, Alpha, Priority, DotTones } from '../../utils/types/index';
/**
 * @cssprop --icon-width - Used for Custom Size Width
 * @cssprop --icon-height - Used for Custom Size Height
 * @cssprop --icon-color - Used for Custom Color of Icon
 **/
@Component({
  tag: 'prism-icon',
  styleUrl: 'prism-icon.css',
  shadow: true,
})
export class Icon {
  private intobs?: IntersectionObserver;
  private cdn = 'https://static.cimcontent.net/common-web-assets/xds_icons/svg/';
  /** @internal */
  @Element() el!: HTMLPrismIconElement;
  /** Reference to the Dialog */
  /** @internal */
  element!: HTMLElement;
  /** Event when Icon has loaded */
  @Event() prismLoad!: EventEmitter;
  /** @internal */
  @State() svgIcon!: string;
  /** @internal */
  @State() private svgContent?: string;
  /** @internal */
  @State() private isVisible = false;
  /** @internal */
  @State() private loaded = false;
  /**
   * Contained: Shows a material container around the icon
   */
  @Prop() contained: boolean = false;
  /**
   * Adds a tone to the contained wrapper of the icon
   */
  @Prop() containedTone: Fills = 'neutral2';
  /** Contained Display will toggle between background tone and border stroke tone */
  @Prop() containedDisplay: 'fill' | 'outline' = 'fill';
  /** Contained Priority */
  @Prop() containedPriority: Priority = 'low';
  /**
   * Specifies the label to use for accessibility. Defaults to the icon name.
   */
  @Prop() label?: string;
  /**
   * Specifies the role: img or presentation
   */
  @Prop() role: 'img' | 'presentation' = 'img';
  /**
   * Enable the Notification Dot
   */
  @Prop() notifications: boolean = false;
  /**
   * Specifies which icon to use from the built-in set of icons.
   */
  @Prop() name: string = '';
  /**
   * The size of the icon.
   */
  @Prop() size: CustomSizes = 'sm';
  /**
   * Tones adds the color to the icon
   */
  @Prop() tone: Fills = 'neutral';
  /**
   * Dot Tone
   */
  @Prop() dotTone: DotTones = 'positive';
  /**
   * Alpha Priority
   */
  @Prop() priority: Alpha = 'primary';
  /**
   * Theme: Outline or Fill
   */
  @Prop() theme?: 'outline' | 'fill' = 'fill';
  /**
   * Path: Local or Absolute path to an SVG
   */
  @Prop() path?: string = '';
  /**
   * If enabled, prism-icon will be loaded lazily when it's visible in the viewport.
   * Default, `false`.
   */
  @Prop() lazy = false;
  /**
   * @description Watch for changes to name prop, call loadIcon()
   */
  @Watch('name')
  nameHandler() {
    this.loadIcon();
  }
  /**
   * @description Watch for changes to path prop, call loadIcon()
   */
  @Watch('path')
  pathHandler() {
    this.loadIcon();
  }
  /**
   * @description Watch for changes to theme prop, call loadIcon()
   */
  @Watch('theme')
  themeHandler() {
    this.loadIcon();
  }
  componentWillLoad() {
    // purposely do not return the promise here because loading
    // the svg file should not hold up loading the app
    // only load the svg if it's visible
    this.waitUntilVisible(this.el, '25%', () => {
      this.isVisible = true;
      this.loadIcon();
    });
  }
  connectedCallback() {
    // Component Data Layer
    metrics('prism-icon');
  }
  disconnectedCallback() {
    if (this.intobs) {
      this.intobs.disconnect();
      this.intobs = undefined;
    }
  }

  private waitUntilVisible(el: HTMLPrismIconElement, rootMargin: string, cb: () => void) {
    if (Build.isBrowser && this.lazy && typeof window !== 'undefined' && (window as any).IntersectionObserver) {
      const io = (this.intobs = new (window as any).IntersectionObserver(
        (data: IntersectionObserverEntry[]) => {
          if (data[0].isIntersecting) {
            io.disconnect();
            this.intobs = undefined;
            cb();
          }
        },
        { rootMargin },
      ));

      io.observe(el);
    } else {
      // browser doesn't support IntersectionObserver
      // so just fallback to always show it
      cb();
    }
  }
  /** @internal */
  formatName(str: string) {
    let process = str.replace(' ', '_').toLocaleLowerCase();
    return process;
  }
  /** @internal */
  async loadIcon() {
    if (this.isVisible && (this.name || this.path)) {
      this.loaded = false; // reset to false
      const fetchPath = this.path ? this.path : `${this.cdn}${this.formatName(this.name)}/ic_${this.formatName(this.name)}_${this.theme}.svg`;
      const response = await fetch(fetchPath);
      if (!response.ok) {
        throw new Error(`PRISM-UI >>> The icon ${this.formatName(this.name)} doesn't exist in ${fetchPath}`);
      }
      const svg = await response.text();
      this.svgContent = svg;
      this.loaded = true;
    }
  }
  componentDidRender() {
    if (this.svgContent) {
      const svg = this.element.querySelector('svg');
      const iconCss = [`size-${this.size}`, `${this.tone}`, `${this.priority}`, 'icon'];

      if (!this.path) {
        // Modifications to the XDS Provided Icons
        svg?.removeAttribute('style');
        svg?.setAttribute('fill', 'currentColor');
      }
      // a11y Changes
      svg?.setAttribute('focusable', 'false');
      svg?.setAttribute('role', this.role);
      svg?.setAttribute('class', '');
      svg?.classList.add(...iconCss);
      if (this.label === undefined) {
        svg?.setAttribute('aria-hidden', 'true');
      } else {
        svg?.removeAttribute('aria-hidden');
        svg?.setAttribute('aria-label', this.label);
      }
      this.prismLoad.emit({ name: this.name || this.path, target: this.element, loaded: this.loaded });
    }
  }
  render() {
    return (
      <Host
        class={{
          loaded: this.loaded,
        }}
      >
        <section
          class={{
            contained: this.contained,
            [this.containedTone]: this.contained,
            [this.containedDisplay]: this.contained,
            [this.containedPriority]: this.contained,
            [this.size]: true,
          }}
        >
          {Build.isBrowser && this.svgContent ? (
            <div
              part="container"
              ref={el => (this.element = el as HTMLElement)}
              class={{
                'notifications': this.notifications,
                [`dot-${this.dotTone}`]: true,
                'icon-inner': true,
                [`size-${this.size}`]: true,
              }}
              innerHTML={this.svgContent}
            ></div>
          ) : null}
        </section>
      </Host>
    );
  }
}
